import React from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Tooltip,
  Chip,
} from '@material-ui/core';
import { PodStatus } from '../types';

interface KubernetesPodsTableProps {
  pods: PodStatus[];
  loading?: boolean;
}

function getStatusColor(status: PodStatus['status']): string {
  switch (status) {
    case 'Running': return '#4caf50';
    case 'Pending': return '#ff9800';
    case 'Failed': return '#f44336';
    default: return '#9e9e9e';
  }
}

/**
 * 获取镜像短名称（去掉 registry 前缀和 digest/tag）
 * registry.com/ns/ebridge@sha256:abc... → ebridge
 * registry.com/ns/ebridge:v1.2.3 → ebridge:v1.2.3
 */
function getImageShortName(image: string): string {
  // 去掉 registry 前缀，取最后一段
  const name = image.split('/').pop() || image;
  // 如果是 digest 格式 (name@sha256:xxx)，只取 name 部分
  if (name.includes('@sha256:')) {
    return name.split('@')[0];
  }
  return name;
}

/**
 * 获取应用容器（排除 Knative sidecar）
 * 优先取 user-container，其次排除 queue-proxy，最后 fallback 到第一个
 */
function getAppContainer(containers: Array<{ name: string; image: string; [key: string]: any }>) {
  return containers.find(c => c.name === 'user-container')
    || containers.find(c => c.name !== 'queue-proxy')
    || containers[0];
}

export const KubernetesPodsTable: React.FC<KubernetesPodsTableProps> = ({ pods, loading }) => {
  return (
    <Card>
      <CardHeader
        title={`Pods (${pods.length})`}
        titleTypographyProps={{ variant: 'subtitle1', style: { fontWeight: 600 } }}
        style={{ paddingBottom: 0 }}
      />
      <CardContent style={{ padding: 0 }}>
        {loading ? (
          <Box p={2}>
            <Typography variant="body2" color="textSecondary">加载中...</Typography>
          </Box>
        ) : pods.length === 0 ? (
          <Box p={2}>
            <Typography variant="body2" color="textSecondary">暂无 Pod</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 80 }}>状态</TableCell>
                <TableCell>名称</TableCell>
                <TableCell style={{ width: 120 }}>镜像</TableCell>
                <TableCell style={{ width: 70 }}>Ready</TableCell>
                <TableCell style={{ width: 80 }}>重启</TableCell>
                <TableCell style={{ width: 80 }}>运行时长</TableCell>
                <TableCell style={{ width: 200 }}>节点</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pods.map(pod => {
                const appContainer = getAppContainer(pod.containers);
                const imageShort = appContainer?.image
                  ? getImageShortName(appContainer.image)
                  : '-';
                const hasRestarts = pod.restarts > 3;
                return (
                  <TableRow key={pod.name} hover>
                    <TableCell>
                      <Chip
                        label={pod.status}
                        size="small"
                        style={{
                          backgroundColor: getStatusColor(pod.status) + '22',
                          color: getStatusColor(pod.status),
                          border: `1px solid ${getStatusColor(pod.status)}44`,
                          fontSize: '0.7rem',
                          height: 20,
                        }}
                      />
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      <Typography
                        variant="body2"
                        style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}
                      >
                        {pod.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={appContainer?.image || ''}>
                        <Typography
                          variant="body2"
                          style={{ fontSize: '0.8rem', fontFamily: 'monospace', color: '#555' }}
                        >
                          {imageShort}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ fontSize: '0.8rem' }}>
                        {pod.ready}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: '0.8rem',
                          fontWeight: hasRestarts ? 600 : 400,
                          color: hasRestarts ? '#f57c00' : 'inherit',
                        }}
                      >
                        {pod.restarts}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ fontSize: '0.8rem', color: '#666' }}>
                        {pod.age}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={pod.node}>
                        <Typography
                          variant="body2"
                          style={{
                            maxWidth: 190,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.75rem',
                            color: '#888',
                          }}
                        >
                          {pod.node}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
